"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight, Crown, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCustomer } from "@/context/CustomerContext";
import {
  fetchMitraOptions,
  fetchMyLatestApprovedApplication,
  type MyApprovedApplication,
} from "@/lib/supabase/mitra";
import {
  MITRA_BANNER_DISMISS_KEY,
  MITRA_CONGRATS_KEY,
  eligibleUpgradeLevels,
  mitraBannerVisible,
  shouldCelebrateApplication,
  shouldShowMitraInvite,
} from "@/lib/mitra";

// ─── Ajakan "Daftar Mitra/Reseller" (mig 3810) ───────────────────────────────
// Dua permukaan dari SATU sumber data (react-query, kunci sama → satu fetch):
//  • MitraHomeBanner  — banner beranda, bisa ditutup 3 hari (localStorage).
//  • MitraAccountCard — kartu ter-highlight di halaman Akun (tak bisa ditutup;
//    saat ada pengajuan pending berubah jadi kartu status).
// Keduanya SENYAP saat tidak relevan: guest, akun non-aktif, sudah level
// tertinggi, toko belum membuka pendaftaran, atau fetch gagal.

function useMitraInvite() {
  const { customer } = useCustomer();
  const query = useQuery({
    queryKey: ["mitra-options", customer?.id ?? null] as const,
    queryFn: fetchMitraOptions,
    enabled: !!customer,
    staleTime: 5 * 60_000,
  });
  const options = query.data ?? null;
  const eligible = options ? eligibleUpgradeLevels(options) : [];
  return {
    customer,
    options,
    eligible,
    invite: shouldShowMitraInvite(options),
    pending: options?.pendingApplication ?? null,
    maxDiscount: eligible.reduce((m, l) => Math.max(m, l.discountPersen), 0),
    isUpgrade: (options?.currentSlot ?? 0) > 0,
  };
}

export function MitraHomeBanner() {
  const { customer, invite, pending, maxDiscount, isUpgrade } =
    useMitraInvite();
  // Stempel tutup dibaca di efek (bukan saat render) — localStorage tak ada di
  // SSR dan beda nilainya memicu hydration mismatch.
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    try {
      setDismissedAt(window.localStorage.getItem(MITRA_BANNER_DISMISS_KEY));
    } catch {
      setDismissedAt(null);
    }
  }, []);

  if (!customer || !mounted) return null;

  // Pengajuan menunggu (mis. baru daftar level dari /daftar, mig 6180) → strip
  // status, tak bisa ditutup: pembeli perlu tahu kenapa harganya masih normal.
  if (pending) {
    return (
      <div className="px-3 pt-3">
        <Link
          href="/mitra"
          className="flex items-center gap-2 rounded-lg border border-marigold/40 bg-marigold/10 px-3 py-2.5"
        >
          <Send size={15} className="shrink-0 text-marigold" />
          <span className="min-w-0 flex-1 text-[11px] font-semibold leading-4 text-muted">
            <strong className="text-marigold">
              Pendaftaran {pending.targetGroupName}: Menunggu Dikonfirmasi
            </strong>{" "}
            — sementara belanja dengan harga normal.
          </span>
          <ChevronRight size={14} className="shrink-0 text-muted" />
        </Link>
      </div>
    );
  }

  if (!invite) return null;
  if (!mitraBannerVisible(dismissedAt, new Date())) return null;

  return (
    <div className="px-3 pt-3">
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-violet-600 via-fuchsia-600 to-shopee p-3 shadow-soft">
        <button
          type="button"
          aria-label="Tutup"
          onClick={() => {
            const now = new Date().toISOString();
            try {
              window.localStorage.setItem(MITRA_BANNER_DISMISS_KEY, now);
            } catch {
              /* abaikan — banner tetap ditutup utk sesi ini */
            }
            setDismissedAt(now);
          }}
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white"
        >
          <X size={12} />
        </button>
        <div className="flex items-center gap-3 pr-6">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/20">
            <Crown size={20} className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold leading-tight text-white">
              {isUpgrade ? "Naik Level Mitra!" : "Gabung jadi Mitra/Reseller!"}
            </p>
            <p className="mt-0.5 text-[11px] font-semibold leading-4 text-white/85">
              {maxDiscount > 0
                ? `Nikmati harga khusus s.d. ${maxDiscount}% lebih murah dari harga normal.`
                : "Nikmati harga khusus untuk mitra terdaftar."}
            </p>
          </div>
          <Link
            href="/mitra"
            className="flex h-9 shrink-0 items-center justify-center rounded-lg bg-white px-3 text-[11px] font-extrabold text-violet-700"
          >
            Daftar
          </Link>
        </div>
      </div>
    </div>
  );
}

export function MitraAccountCard() {
  const { customer, invite, pending, maxDiscount, isUpgrade } = useMitraInvite();
  if (!customer) return null;

  if (pending) {
    return (
      <Link
        href="/mitra"
        className="flex items-center gap-3 overflow-hidden rounded-xl border border-marigold/40 bg-marigold/10 p-4 shadow-soft"
      >
        <Send size={18} className="shrink-0 text-marigold" />
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold text-ink">
            Pendaftaran {pending.targetGroupName}: Menunggu Dikonfirmasi
          </span>
          <span className="block text-[11px] font-semibold text-muted">
            Sementara ini kamu Customer Umum (harga normal).
          </span>
        </span>
        <ChevronRight size={16} className="shrink-0 text-muted" />
      </Link>
    );
  }

  if (!invite) return null;

  return (
    <Link
      href="/mitra"
      className="relative flex items-center gap-3 overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-shopee p-4 shadow-soft"
    >
      <span className="absolute right-3 top-2 rounded-full bg-white/25 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white">
        Baru
      </span>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/20">
        <Crown size={20} className="text-white" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-extrabold text-white">
          {isUpgrade ? "Naik Level Mitra" : "Daftar Mitra / Reseller"}
        </span>
        <span className="block text-[11px] font-semibold text-white/85">
          {maxDiscount > 0
            ? `Harga khusus s.d. ${maxDiscount}% lebih murah — daftar sekarang.`
            : "Dapatkan harga khusus mitra — daftar sekarang."}
        </span>
      </span>
      <ChevronRight size={16} className="shrink-0 text-white" />
    </Link>
  );
}

/**
 * Popup SEKALI SAJA "Selamat, kamu resmi jadi mitra" — muncul begitu pengajuan
 * mitra pelanggan disetujui (level naik), lalu distempel di localStorage
 * (`MITRA_CONGRATS_KEY` = id pengajuan terakhir yang dirayakan) supaya tak
 * pernah tampil dua kali; naik level BERIKUTNYA (pengajuan baru) dirayakan
 * lagi. Dipasang GLOBAL di Providers (pola PopupDisplay) karena approval
 * terjadi saat pelanggan tidak sedang membuka halaman /mitra — popup menemui
 * mereka di halaman mana pun ia membuka toko. Jalur GRATIS tidak dobel
 * perayaan: MitraClient menstempel id pengajuannya langsung (layar suksesnya
 * sendiri sudah perayaan).
 */
export function MitraCongratsPopup() {
  const pathname = usePathname();
  const { customer } = useCustomer();
  const [celebrating, setCelebrating] = useState<MyApprovedApplication | null>(
    null
  );

  // Panel admin/platform & katalog reseller white-label bukan tempatnya.
  const excluded =
    !pathname ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/platform") ||
    pathname.startsWith("/toko");

  useEffect(() => {
    if (!customer || excluded) return;
    let alive = true;
    fetchMyLatestApprovedApplication()
      .then((app) => {
        if (!alive || !app) return;
        let seen: string | null = null;
        try {
          seen = window.localStorage.getItem(MITRA_CONGRATS_KEY);
        } catch {
          seen = null;
        }
        if (shouldCelebrateApplication(app.id, seen)) setCelebrating(app);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, excluded]);

  if (!celebrating || excluded) return null;

  const close = () => {
    try {
      window.localStorage.setItem(MITRA_CONGRATS_KEY, celebrating.id);
    } catch {
      /* stempel gagal → paling buruk tampil lagi di kunjungan berikut */
    }
    setCelebrating(null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-6"
      role="dialog"
      aria-modal="true"
      aria-label="Selamat menjadi mitra"
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-violet-600 to-shopee">
          <Crown size={30} className="text-white" />
        </div>
        <p className="mt-3 text-2xl">🎉</p>
        <h2 className="mt-1 text-lg font-extrabold text-ink">
          Selamat, kamu resmi jadi {celebrating.targetGroupName}!
        </h2>
        <p className="mt-1.5 text-xs font-semibold leading-5 text-muted">
          Pendaftaran mitra kamu sudah disetujui. Harga khusus{" "}
          {celebrating.targetGroupName} kini aktif otomatis di semua produk —
          selamat belanja!
        </p>
        <Link
          href="/"
          onClick={close}
          className="mt-4 flex h-11 w-full items-center justify-center rounded-lg bg-shopee text-sm font-extrabold text-white shadow-soft"
        >
          Mulai Belanja
        </Link>
        <button
          type="button"
          onClick={close}
          className="mt-2 flex h-10 w-full items-center justify-center rounded-lg border border-neutral-100 bg-white text-xs font-bold text-muted"
        >
          Tutup
        </button>
      </div>
    </div>
  );
}
